import React, {
  type ComponentProps,
  forwardRef,
  useCallback,
  useRef,
} from 'react';
import {
  Pressable,
  type PressableStateCallbackType,
  type StyleProp,
  StyleSheet,
  type View,
  type ViewStyle,
} from 'react-native';
import useMergedRef from '@react-hook/merged-ref';
import { theme } from '@garnish/constants';

import { useBrowserEventListener } from '../../../../hooks';
import { webOnlyStyles } from '../../../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientCardPressable = forwardRef<
  View,
  IngredientCardPressableProps
>((props, ref) => {
  const {
    style,
    disabled,
    accessibilityLabel,
    isMuted,
    onKeyDown,
    ...restProps
  } = props;

  const localRef = useRef<View>(null);
  const mergedRef = useMergedRef(localRef, ref);

  // ─── Styles ──────────────────────────────────────────────────────────

  const pressableStyles = useCallback(
    (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
      const { pressed, hovered } = state as InteractionState;

      return [
        styles.pressable,
        pressableWebStyles,
        hovered && !disabled ? styles.pressableHover : undefined,
        pressed && !disabled ? styles.pressablePressed : undefined,
        isMuted ? styles.pressableMuted : undefined,
        style,
      ];
    },
    [disabled, isMuted, style],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleKeyDown = useCallback(
    (event: Event) => {
      const castedEvent = event as KeyboardEvent;
      const castedRef = localRef.current as unknown as HTMLElement;
      const castedTarget = event.target as Node;
      const isValidTarget =
        castedTarget === castedRef || castedRef?.contains?.(castedTarget);

      if (!isValidTarget) {
        return;
      }

      onKeyDown?.(castedEvent);
    },
    [onKeyDown],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useBrowserEventListener(localRef, 'keydown', handleKeyDown);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={mergedRef}
      accessibilityLabel={accessibilityLabel}
      role="button"
      style={pressableStyles}
      disabled={disabled}
      {...restProps}
    />
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    gap: theme.spacing['2'],
  },
  pressableHover: {
    opacity: 0.8,
  },
  pressablePressed: {
    opacity: 0.6,
  },
  pressableMuted: {
    opacity: 0.5,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: theme.transitions.presets.colorsAndOpacity,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type IngredientCardPressableProps = {
  style?: ViewStyle;
  accessibilityLabel?: string;
  isMuted?: boolean;
  onKeyDown?: (event: KeyboardEvent) => void;
} & Omit<ComponentProps<typeof Pressable>, 'style'>;

type InteractionState = {
  hovered: boolean;
} & PressableStateCallbackType;
